<template>
  <div class="category-management" dir="rtl">
    <div class="container">
      <!-- Header Section -->
      <header class="header-section">
        <h1 class="page-title">إدارة التصنيفات</h1>
        <p class="page-subtitle">قم بإدارة تصنيفات موقعك بسهولة وفعالية</p>
        <button class="btn-add" @click="openModalForAdd">
          <span class="btn-icon">+</span>
          إضافة تصنيف جديد
        </button>
      </header>

      <!-- Modal -->
      <transition name="modal">
        <div v-if="showCategoryModal" class="modal-overlay" @click.self="closeCategoryModal">
          <div class="modal-content">
            <div class="modal-header">
              <h2 class="modal-title">
                {{ isEditMode ? "تعديل التصنيف" : "إضافة تصنيف جديد" }}
              </h2>
              <button class="modal-close" @click="closeCategoryModal">&times;</button>
            </div>
            
            <form @submit.prevent="handleSubmit" enctype="multipart/form-data" class="category-form">
              <div class="form-group">
                <label for="name" class="form-label">اسم التصنيف</label>
                <input
                  type="text"
                  id="name"
                  class="form-control"
                  v-model="form.name"
                  placeholder="أدخل اسم التصنيف"
                  required
                />
              </div>

              <div class="form-group">
                <label for="icon" class="form-label">أيقونة التصنيف</label>
                <div 
                  class="file-upload-container" 
                  @click="$refs.iconInput.click()"
                  @dragover.prevent
                  @drop.prevent="handleFileDrop"
                >
                  <input
                    type="file"
                    id="icon"
                    ref="iconInput"
                    class="file-input"
                    @change="handleFileChange"
                    :required="!isEditMode"
                    accept="image/*"
                  />
                  <div class="upload-placeholder">
                    <span class="upload-icon">↑</span>
                    <span>اضغط للرفع أو اسحب وأفلت</span>
                    <span class="upload-hint">PNG, JPG حتى 5MB</span>
                  </div>
                  <div v-if="form.icon" class="file-preview">
                    <img :src="previewUrl" alt="معاينة" class="preview-image" />
                    <button type="button" class="remove-file" @click.stop="removeFile">×</button>
                  </div>
                </div>
              </div>

              <div class="modal-actions">
                <button type="submit" class="btn-submit" :disabled="isSubmitting">
                  <span v-if="isSubmitting" class="spinner"></span>
                  {{ isEditMode ? "تحديث التصنيف" : "إضافة التصنيف" }}
                </button>
                <button type="button" class="btn-cancel" @click="closeCategoryModal">
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      </transition>

      <!-- Table Section -->
      <div class="table-section">
        <div class="table-header">
          <h2 class="table-title">قائمة التصنيفات</h2>
          <div class="table-actions">
            <div class="search-container">
              <input 
                type="text" 
                placeholder="بحث..." 
                class="search-input"
                v-model="searchQuery"
              />
              <span class="search-icon">🔍</span>
            </div>
          </div>
        </div>

        <div class="table-container">
          <table class="categories-table">
            <thead>
              <tr>
                <th>التسلسل</th>
                <th>اسم التصنيف</th>
                <th>الأيقونة</th>
                <th>تاريخ الإنشاء</th>
                <th>الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(category, index) in filteredCategories" 
                  :key="category.id"
                  class="table-row"
                  :class="{ 'row-fade': category.isDeleting }"
              >
                <td>{{ (pageNumber - 1) * pageSize + (index + 1) }}</td>
                <td>{{ category.name }}</td>
                <td class="icon-cell">
                  <img
                    v-if="category.thisIconFullUrl"
                    :src="category.thisIconFullUrl"
                    :alt="category.name"
                    class="category-icon"
                    @error="handleImageError"
                  />
                  <span v-else class="no-icon">لا توجد أيقونة</span>
                </td>
                <td>{{ formatDate(category.createdAt) }}</td>
                <td class="actions-cell">
                  <button class="btn-action edit" @click="openModalForEdit(category)">
                    <span class="action-icon">✎</span>
                    تعديل
                  </button>
                  <button class="btn-action delete" @click="deleteCategory(category.id)">
                    <span class="action-icon">×</span>
                    حذف
                  </button>
                </td>
              </tr>
              <tr v-if="filteredCategories.length === 0">
                <td colspan="5" class="empty-state">
                  لا توجد تصنيفات متاحة
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div class="pagination-container">
          <custom-pagination
            :currentPage="pageNumber"
            :totalPages="totalPages"
            @page-changed="handlePageChange"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from "@/api";
import CustomPagination from "@/components/CustomPagination.vue";
import Swal from "sweetalert2";

export default {
  name: "CategoryManagement",
  components: { CustomPagination },
  data() {
    return {
      categories: [],
      pageNumber: 1,
      pageSize: 10,
      totalPages: 1,
      showCategoryModal: false,
      isEditMode: false,
      isSubmitting: false,
      searchQuery: "",
      previewUrl: null,
      form: {
        id: null,
        name: "",
        icon: null,
      },
    };
  },
  computed: {
    filteredCategories() {
      if (!this.searchQuery) return this.categories;
      const query = this.searchQuery.toLowerCase();
      return this.categories.filter(category => 
        category.name.toLowerCase().includes(query)
      );
    }
  },
  methods: {
    async getAllCategories() {
      try {
        const response = await apiClient.get("/api/Categories/GetAllCategories", {
          params: {
            PageNumber: this.pageNumber,
            PageSize: this.pageSize,
          },
        });

        const d = response.data;
        this.categories = d.items ?? [];
        this.totalPages = Math.max(1, Number(d.totalPages) || 1);
        // رقم الصفحة يأتي من المستخدم (handlePageChange)، لا نستبدله بـ pageNumber من الـ API إن كان ثابتاً أو خاطئاً
        if (this.pageNumber > this.totalPages) {
          this.pageNumber = this.totalPages;
        }
      } catch (error) {
        this.showError("حدث خطأ أثناء جلب التصنيفات");
      }
    },

    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('ar-SA', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },

    handleFileDrop(event) {
      const file = event.dataTransfer.files[0];
      if (file && file.type.startsWith('image/')) {
        this.handleFile(file);
      }
    },

    handleFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.handleFile(file);
      }
    },

    handleFile(file) {
      if (file.size > 5 * 1024 * 1024) {
        this.showError("حجم الملف يجب أن يكون أقل من 5 ميجابايت");
        return;
      }
      this.form.icon = file;
      this.previewUrl = URL.createObjectURL(file);
    },

    removeFile() {
      this.form.icon = null;
      this.previewUrl = null;
      if (this.$refs.iconInput) {
        this.$refs.iconInput.value = '';
      }
    },

    handleImageError(event) {
      console.log(event);
    },

    async handleSubmit() {
      this.isSubmitting = true;
      const formData = new FormData();
      formData.append("Name", this.form.name);
      if (this.form.icon) {
        formData.append("Icon", this.form.icon);
      }

      try {
        if (this.isEditMode) {
          await apiClient.put("/api/Categories/UpdateCategory", formData, {
            params: { id: this.form.id },
            headers: { "Content-Type": "multipart/form-data" },
          });
          this.showSuccess("تم تحديث التصنيف بنجاح");
        } else {
          await apiClient.post("/api/Categories/AddCategory", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          this.showSuccess("تم إضافة التصنيف بنجاح");
        }
        this.closeCategoryModal();
        this.getAllCategories();
      } catch (error) {
        this.showError("حدث خطأ أثناء معالجة العملية");
      } finally {
        this.isSubmitting = false;
      }
    },

    showError(message) {
      Swal.fire({
        icon: "error",
        title: "خطأ",
        text: message,
        timer: 3000,
        showConfirmButton: false,
        customClass: {
          popup: 'error-modal'
        }
      });
    },

    showSuccess(message) {
      Swal.fire({
        icon: "success",
        title: "تم",
        text: message,
        timer: 2000,
        showConfirmButton: false,
        customClass: {
          popup: 'success-modal'
        }
      });
    },

    async deleteCategory(categoryId) {
      const result = await Swal.fire({
        title: "تأكيد الحذف",
        text: "هل أنت متأكد من حذف هذا التصنيف؟",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#dc3545",
        cancelButtonColor: "#6c757d",
        confirmButtonText: "نعم، احذفه",
        cancelButtonText: "إلغاء",
      });

      if (result.isConfirmed) {
        try {
          // تحديث حالة الصف قبل الحذف
          const category = this.categories.find(c => c.id === categoryId);
          if (category) {
            category.isDeleting = true;
          }

          await apiClient.delete("/api/Categories/DeleteCategory", {
            params: { categoryId },
          });
          
          this.showSuccess("تم حذف التصنيف بنجاح");
          await this.getAllCategories();
        } catch (error) {
          this.showError("حدث خطأ أثناء حذف التصنيف");
          // إعادة الحالة في حالة الخطأ
          const category = this.categories.find(c => c.id === categoryId);
          if (category) {
            category.isDeleting = false;
          }
        }
      }
    },

    openModalForAdd() {
      this.isEditMode = false;
      this.resetForm();
      this.showCategoryModal = true;
    },

    openModalForEdit(category) {
      this.isEditMode = true;
      this.form.id = category.id;
      this.form.name = category.name;
      this.form.icon = null;
      this.previewUrl = category.thisIconFullUrl;
      this.showCategoryModal = true;
    },

    closeCategoryModal() {
      this.showCategoryModal = false;
      this.resetForm();
    },

    resetForm() {
      this.isEditMode = false;
      this.form = {
        id: null,
        name: "",
        icon: null,
      };
      this.previewUrl = null;
      if (this.$refs.iconInput) {
        this.$refs.iconInput.value = "";
      }
    },

    handlePageChange(newPage) {
      this.pageNumber = newPage;
      this.getAllCategories();
    },
  },
  mounted() {
    this.getAllCategories();
  },
};
</script>

<style>
.category-management {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 2rem;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
}

/* Header Styles */
.header-section {
  text-align: center;
  margin-bottom: 3rem;
  background: white;
  padding: 2rem;
  border-radius: 1rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  animation: slideDown 0.5s ease-out;
}

.page-title {
  font-size: 1.5rem;
  color: #2d3748;
  margin-bottom: 0.5rem;
  font-weight: 400;
}

.page-subtitle {
  color: #718096;
  margin-bottom: 1.5rem;
}

.btn-add {
  background: #4299e1;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-add:hover {
  background: #3182ce;
  transform: translateY(-1px);
}

/* Modal Styles */
.modal-overlay {
  background: rgba(0, 0, 0, 0.5);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
  backdrop-filter: blur(4px);
}

.modal-content {
  background: white;
  border-radius: 1rem;
  width: 90%;
  max-width: 500px;
  position: relative;
  padding: 2rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  animation: modalSlideIn 0.3s ease-out;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.modal-title {
  font-size: 1.5rem;
  color: #2d3748;
  font-weight: 600;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #718096;
  cursor: pointer;
  padding: 0.5rem;
  transition: color 0.2s;
}

.modal-close:hover {
  color: #2d3748;
}

/* Form Styles */
.form-group {
  margin-bottom: 1.5rem;
}

.form-label {
  display: block;
  margin-bottom: 0.5rem;
  color: #4a5568;
  font-weight: 500;
}

.form-control {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  font-size: 1rem;
  transition: all 0.3s ease;
}

.form-control:focus {
  border-color: #4299e1;
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.2);
  outline: none;
}

.file-upload-container {
  border: 2px dashed #e2e8f0;
  border-radius: 0.5rem;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.file-upload-container:hover {
  border-color: #4299e1;
  background: #ebf8ff;
}

.file-input {
  display: none;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.upload-icon {
  font-size: 1.0rem;
  color: #4299e1;
}

.upload-hint {
  font-size: 0.875rem;
  color: #718096;
}

.file-preview {
  position: relative;
  margin-top: 1rem;
}

.preview-image {
  max-width: 100%;
  max-height: 200px;
  border-radius: 0.5rem;
}

.remove-file {
  position: absolute;
  top: -0.5rem;
  right: -0.5rem;
  background: #fc8181;
  color: white;
  border: none;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
}

.remove-file:hover {
  background: #f56565;
}

/* Table Styles */
.table-section {
  background: white;
  border-radius: 1rem;
  padding: 1.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  animation: fadeIn 0.5s ease-out;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.search-container {
  position: relative;
}

.search-input {
  padding: 0.5rem 1rem;
  padding-right: 2.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  width: 250px;
  transition: all 0.3s ease;
}

.search-input:focus {
  border-color: #4299e1;
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.2);
  outline: none;
}

.search-icon {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #718096;
}

.categories-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.categories-table th {
  background: #f7fafc;
  padding: 1rem;
  font-weight: 600;
  color: #4a5568;
  text-align: right;
  border-bottom: 2px solid #e2e8f0;
}

.categories-table td {
  padding: 1rem;
  border-bottom: 1px solid #e2e8f0;
  transition: background-color 0.2s;
}

.table-row:hover td {
  background-color: #f7fafc;
}

.icon-cell {
  width: 60px;
}

.category-icon {
  width: 40px;
  height: 40px;
  border-radius: 0.5rem;
  object-fit: cover;
}

.actions-cell {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}

.btn-action {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.875rem;
  transition: all 0.3s ease;
}

.btn-action.edit {
  background: #faf089;
  color: #744210;
}

.btn-action.edit:hover {
  background: #f6e05e;
}

.btn-action.delete {
  background: #fed7d7;
  color: #c53030;
}

.btn-action.delete:hover {
  background: #feb2b2;
}

.empty-state {
  text-align: center;
  padding: 2rem;
  color: #718096;
}

/* Modal Actions */
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
}

.btn-submit {
  background: #4299e1;
  color: white;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: background-color 0.3s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-submit:hover:not(:disabled) {
  background: #3182ce;
}

.btn-submit:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-cancel {
  background: #e2e8f0;
  color: #4a5568;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-cancel:hover {
  background: #cbd5e0;
}

/* Spinner */
.spinner {
  width: 1rem;
  height: 1rem;
  border: 2px solid #ffffff;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

/* Animations */
@keyframes spin {
  100% {
    transform: rotate(360deg);
  }
}

@keyframes modalSlideIn {
  from {
    transform: translateY(-1rem);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes slideDown {
  from {
    transform: translateY(-1rem);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Row Deletion Animation */
.row-fade {
  animation: rowFadeOut 0.3s ease-out forwards;
}

@keyframes rowFadeOut {
  to {
    opacity: 0;
    transform: translateX(1rem);
  }
}

/* Modal Transitions */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

/* Responsive Design */
@media (max-width: 768px) {
  .category-management {
    padding: 1rem;
  }

  .table-header {
    flex-direction: column;
    gap: 1rem;
  }

  .search-input {
    width: 100%;
  }

  .actions-cell {
    flex-direction: column;
  }

  .btn-action {
    width: 100%;
    justify-content: center;
  }

  .modal-content {
    padding: 1.5rem;
    margin: 1rem;
  }

  .categories-table th:not(:first-child):not(:last-child),
  .categories-table td:not(:first-child):not(:last-child) {
    display: none;
  }
}

@media (max-width: 480px) {
  .page-title {
    font-size: 1.0rem;
  }

  .modal-actions {
    flex-direction: column;
  }

  .btn-submit,
  .btn-cancel {
    width: 100%;
  }
}

/* RTL Specific Adjustments */
[dir="rtl"] .search-icon {
  right: auto;
  left: 0.75rem;
}

[dir="rtl"] .modal-actions {
  flex-direction: row-reverse;
}

[dir="rtl"] .actions-cell {
  justify-content: flex-start;
}

[dir="rtl"] .remove-file {
  right: auto;
  left: -0.5rem;
}
</style>